import type { RevenueLockdownSchema } from '@/_pages/settings/revenue-lockdowns/shared';
import { dateTime } from '@/_shared';

import { fieldNames } from './converters/field-names-converter';
import { getRevenueLockdownIdsForDateChange } from './revenue-lockdown-guard';
import type { AgreementFormSchema } from './schemas';

type DateValue = string | null | undefined;
type DirtyFields = Record<string, unknown>;
type DateRangeMs = { startMs: number; endMs: number };

function isDirty(dirtyFields: unknown, key: keyof AgreementFormSchema): boolean {
  const df = dirtyFields as DirtyFields;
  return Boolean(df[key as string]);
}

function isAnyDirty(dirtyFields: unknown, keys: Array<keyof AgreementFormSchema>): boolean {
  return keys.some((k) => isDirty(dirtyFields, k));
}

function isLocked(lockdowns: RevenueLockdownSchema[], start: DateValue, end: DateValue): boolean {
  const ids = getRevenueLockdownIdsForDateChange({
    lockdowns,
    oldStart: start,
    oldEnd: end,
    newStart: start,
    newEnd: end,
  });
  return ids.length > 0;
}

function toStartMs(date: string): number {
  return dateTime.parse(date).startOf('day').valueOf();
}

function toEndMs(date: string): number {
  return dateTime.parse(date).endOf('day').valueOf();
}

function toRangeMs(start: DateValue, end: DateValue): DateRangeMs | null {
  const s = start ?? null;
  const e = end ?? null;

  if (!s && !e) return null;

  if (s && !e) {
    return { startMs: toStartMs(s), endMs: Number.POSITIVE_INFINITY };
  }
  if (!s && e) {
    return { startMs: toStartMs(e), endMs: toEndMs(e) };
  }

  return { startMs: toStartMs(s as string), endMs: toEndMs(e as string) };
}

function rangesIntersect(a: DateRangeMs, b: DateRangeMs): boolean {
  return a.startMs <= b.endMs && b.startMs <= a.endMs;
}

function lockdownToRange(lockdown: RevenueLockdownSchema): DateRangeMs {
  return {
    startMs: toStartMs(lockdown.lock_start_date),
    endMs: toEndMs(lockdown.lock_end_date),
  };
}

function isRangeLocked(lockdowns: RevenueLockdownSchema[], range: DateRangeMs): boolean {
  return lockdowns.some((lockdown) => rangesIntersect(range, lockdownToRange(lockdown)));
}

function getChangedRangeSegments(currentRange: DateRangeMs, newRange: DateRangeMs): DateRangeMs[] {
  const segments: DateRangeMs[] = [];

  if (newRange.startMs < currentRange.startMs) {
    segments.push({ startMs: newRange.startMs, endMs: currentRange.startMs - 1 });
  }
  if (newRange.endMs > currentRange.endMs) {
    segments.push({ startMs: currentRange.endMs + 1, endMs: newRange.endMs });
  }
  if (currentRange.startMs < newRange.startMs) {
    segments.push({ startMs: currentRange.startMs, endMs: newRange.startMs - 1 });
  }
  if (currentRange.endMs > newRange.endMs) {
    segments.push({ startMs: newRange.endMs + 1, endMs: currentRange.endMs });
  }

  return segments.filter((segment) => segment.startMs <= segment.endMs);
}

function isRecurringRangeChangeLocked(args: {
  lockdowns: RevenueLockdownSchema[];
  currentStart: DateValue;
  currentEnd: DateValue;
  newStart: DateValue;
  newEnd: DateValue;
}): boolean {
  const currentRange = toRangeMs(args.currentStart, args.currentEnd);
  const newRange = toRangeMs(args.newStart, args.newEnd);

  if (!currentRange) {
    return newRange ? isRangeLocked(args.lockdowns, newRange) : false;
  }

  if (!newRange) {
    return isRangeLocked(args.lockdowns, currentRange);
  }

  return getChangedRangeSegments(currentRange, newRange).some((segment) => isRangeLocked(args.lockdowns, segment));
}

function labelFor(key: keyof AgreementFormSchema): string {
  const qKey = key as unknown as keyof typeof fieldNames;
  return fieldNames[qKey]?.name ?? String(key);
}

/**
 * Collects the form field keys affected by revenue lockdown based on current form values.
 * Only considers fields that are dirty (or part of a dirty pair) so users can save without
 * being blocked by unchanged locked values.
 */
export function getLockedAgreementExecutionFieldKeys(args: {
  data: AgreementFormSchema;
  currentData?: AgreementFormSchema | null;
  dirtyFields: unknown;
  lockdowns: RevenueLockdownSchema[];
}): Array<keyof AgreementFormSchema> {
  const { data, currentData, dirtyFields, lockdowns } = args;
  const keys = new Set<keyof AgreementFormSchema>();

  const singleDateKeys: Array<keyof AgreementFormSchema> = [
    'execution_date',
    'implementation_start_date',
    'go_live_date',
  ];

  for (const key of singleDateKeys) {
    if (!isDirty(dirtyFields, key)) continue;
    const v = data[key] as DateValue;
    if (!v) continue;
    if (isLocked(lockdowns, v, v)) keys.add(key);
  }

  if (isAnyDirty(dirtyFields, ['monthly_recurring_start_date', 'monthly_recurring_stop_date'])) {
    if (
      isRecurringRangeChangeLocked({
        lockdowns,
        currentStart: currentData?.monthly_recurring_start_date,
        currentEnd: currentData?.monthly_recurring_stop_date,
        newStart: data.monthly_recurring_start_date,
        newEnd: data.monthly_recurring_stop_date,
      })
    ) {
      if (isDirty(dirtyFields, 'monthly_recurring_start_date')) {
        keys.add('monthly_recurring_start_date');
      }
      if (isDirty(dirtyFields, 'monthly_recurring_stop_date')) {
        keys.add('monthly_recurring_stop_date');
      }
    }
  }

  if (isAnyDirty(dirtyFields, ['annual_recurring_start_date', 'annual_recurring_stop_date'])) {
    if (
      isRecurringRangeChangeLocked({
        lockdowns,
        currentStart: currentData?.annual_recurring_start_date,
        currentEnd: currentData?.annual_recurring_stop_date,
        newStart: data.annual_recurring_start_date,
        newEnd: data.annual_recurring_stop_date,
      })
    ) {
      if (isDirty(dirtyFields, 'annual_recurring_start_date')) {
        keys.add('annual_recurring_start_date');
      }
      if (isDirty(dirtyFields, 'annual_recurring_stop_date')) {
        keys.add('annual_recurring_stop_date');
      }
    }
  }

  return Array.from(keys);
}

/**
 * Collects field labels affected by revenue lockdown based on current form values.
 * Only considers fields that are dirty (or part of a dirty pair) so users can save without
 * being blocked by unchanged locked values.
 */
export function getLockedAgreementExecutionFieldLabels(args: {
  data: AgreementFormSchema;
  currentData?: AgreementFormSchema | null;
  dirtyFields: unknown;
  lockdowns: RevenueLockdownSchema[];
}): string[] {
  return getLockedAgreementExecutionFieldKeys(args).map(labelFor);
}
